import { Page } from "../../layout/Layout";
import { SectionPromo } from "../../shared/sections/SectionPromo";
// import { LandingSections } from "./LandingSections";
import { useLandingSinglePageContext } from "@/helpers/contexts/LandingSinglePageContext";
import { orderLink } from "@/const";
import { BreadcrumbJsonLd } from "next-seo";
import crypto from "crypto";
import { AILanding, AIServicesSection, AIWritersSection } from "@/types/strapi/ai-landing";
import SectionWriters from "@/components/shared/sections/SectionWriters";
import writers from "./static/writers.json";
import reviews from "./static/reviews.json";
import featureCard from "./static/feature-card.json";
import SectionLandingBenefits from "@/components/shared/sections/SectionLandingBenefits";
import SectionTabList from "@/components/shared/sections/SectionTabList";
import SectionReviews from "@/components/shared/sections/SectionReviews";
import SectionPayments from "@/components/shared/sections/SectionPayments";
import SectionFeatures from "@/components/shared/sections/SectionFeatures";
import SectionLandingHow from "@/components/shared/sections/SectionLandingHow";
import SectionSEO from "@/components/shared/sections/SectionSEO";
import SectionFaq from "@/components/shared/sections/SectionFaq";
import SectionOrder from "@/components/shared/sections/SectionOrder";

const calculateLandingReviews = (url: string) => {
  const hash = crypto.createHash("sha256").update(url).digest("hex");

  const hashValue1 =
    parseInt(hash, 16) / parseInt("ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff", 16);

  const hashValue2 = parseInt(hash, 16);

  const ratingValue = Math.floor(47 + hashValue1 * (50 - 47 + 1)) / 10;
  const reviewCount = 500 + (hashValue2 % (2500 - 500 + 1));

  return {
    ratingValue,
    reviewCount,
  };
};

type LandingSinglePageProps = AILanding;

const AILandingPage = (data: LandingSinglePageProps) => {
  const { content, slug } = data;

  const { meta, promo, sections } = content;

  return (
    <Page {...meta} metaReviews={calculateLandingReviews(slug)}>
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: "Main", item: "/" },
          {
            position: 2,
            name: promo.heading,
            item: `/${slug}`,
          },
        ]}
      />

      <SectionPromo
        heading={promo.heading}
        subheading={promo.subheading}
        advantages={promo.highlights}
        type="calculator"
        button={{
          text: promo.buttonText || "Order now",
          props: {
            href: orderLink,
          },
        }}
        afterButtonText="Trusted by 437,286 satisfied students"
        reviews={{
          items: [
            {
              type: "studycrumb",
              rate: 4.7,
            },
            {
              type: "trustpilot",
              rate: 4.2,
            },
            {
              type: "reviews",
              rate: 4.6,
            },
            {
              type: "sitejabber",
              rate: 4.9,
            },
          ],
        }}
      />
      {sections.map(({ type, ...rest }, index) => {
        let data: any;
        switch (type) {
          case "writers":
            return <SectionWriters key={`${index}`} {...(rest as AIWritersSection)} writers={writers} />;
          case "benefits":
            data = rest;

            return (
              <SectionLandingBenefits
                key={`${index}`}
                heading={data.heading}
                subheading={data.subheading}
                items={data.benefits}
              />
            );
          case "services":
            data = rest;
            return (
              <SectionTabList
                key={`${index}`}
                heading={data.heading}
                subheading={data.subheading}
                tabs={{
                  items: data.serviceGroups.map(({ name, services }) => ({
                    name,
                    list: services,
                  })),
                }}
              />
            );
          case "reviews":
            data = rest;
            return (
              <SectionReviews
                key={`${index}`}
                heading={data.heading}
                subheading={data.subheading}
                reviews={reviews}
              />
            );
          case "payments":
            data = rest;
            return (
              <SectionPayments
                key={`${index}`}
                type="secondary"
                heading={data.heading}
                button={{
                  text: data.buttonText,
                  href: orderLink,
                }}
              />
            );
          case "features":
            data = rest;
            return (
              <SectionFeatures
                key={`${index}`}
                heading={data.heading}
                subheading={data.subheading}
                cards={data.features.map(({ name, text }) => ({
                  heading: name,
                  text,
                }))}
                featureCard={featureCard as any}
              />
            );
          case "steps":
            data = rest;
            return (
              <SectionLandingHow
                key={`${index}`}
                heading={data.heading}
                subheading={data.subheading}
                items={data.steps}
                button={{
                  href: orderLink,
                  text: data.buttonText,
                }}
              />
            );
          case "seo":
            data = rest;
            return (
              <SectionSEO
                key={`${index}`}
                heading={data.heading}
                subheading={data.subheading}
                content={data.content}
                headingCircles
                plainHTML
              />
            );
          case "faq":
            data = rest;
            return (
              <SectionFaq
                key={`${index}`}
                heading={data.heading}
                subheading={data.subheading}
                faqItems={data.items.map(({ question, answer }, index) => ({
                  heading: `${index + 1}. ${question}`,
                  content: answer,
                }))}
                microData
              />
            );
          case "cta":
            data = rest;
            return (
              <SectionOrder
                key={`${index}`}
                heading={data.heading}
                subheading={data.subheading}
                button={{
                  text: data.buttonText,
                  href: orderLink,
                }}
              />
            );
          default:
            return null;
        }
      })}
    </Page>
  );
};

export default AILandingPage;
